// ---------------------------------------------------------------------------
// O que o raio "vê" com cortes activos.
//
// A caixa de corte e os planos soltos escrevem-se em
// `renderer.clippingPlanes`, com a normal virada para DENTRO (para o lado
// que fica à vista). O three corta tudo o que está do lado negativo de
// QUALQUER plano, ou seja, o que se vê é a intersecção dos semiespaços
// positivos: uma região convexa.
//
// O raio do Fragments não sabia disto (ver docs/CAIXA_DE_CORTE.md): sem
// planos entregues ao modelo, devolvia o primeiro impacto na geometria,
// mesmo que esse troço estivesse cortado e invisível. Era assim que o
// cursor pré-seleccionava uma porta escondida fora da caixa e o clique
// a seleccionava sem realce nenhum à vista.
//
// Agora o modelo recebe os planos (o Fragments filtra no worker) e, por
// cima disso, o lado da aplicação filtra também com estas funções. A
// segunda camada cobre o intervalo entre mexer na caixa e o worker
// receber a vista nova, e serve de rede se uma versão futura do motor
// deixar de filtrar.
//
// Puro: só `three` (Vector3 e Plane), sem DOM nem viewer.
// ---------------------------------------------------------------------------

/** O mínimo de um plano que estas funções usam: normal e constante, como
 *  no `THREE.Plane` (n·p + c = 0; o `THREE.Plane` serve tal como está). */
export interface PlanoNormalConstante {
  normal: PontoXYZ
  constant: number
}

export interface PontoXYZ {
  x: number
  y: number
  z: number
}

/** Tolerância, em unidades do mundo (metros), para um ponto exactamente
 *  em cima de um plano de corte contar como visível. Uma face que
 *  coincide com a face da caixa vê-se (o three só corta o lado
 *  estritamente negativo) e o arredondamento do worker não pode deitá-la
 *  fora. Um décimo de milímetro chega e não deixa passar nada que se
 *  note. */
export const TOLERANCIA_CORTE = 1e-4

/** O ponto está do lado visível de todos os planos? Sem planos, tudo é
 *  visível. */
export function pontoVisivel(
  p: PontoXYZ,
  planos: readonly PlanoNormalConstante[],
  tolerancia: number = TOLERANCIA_CORTE,
): boolean {
  for (const { normal: n, constant: c } of planos) {
    if (n.x * p.x + n.y * p.y + n.z * p.z + c < -tolerancia) return false
  }
  return true
}

/** Só os impactos que caem na parte visível. Mantém a ordem. */
export function filtrarImpactosVisiveis<T extends { point: PontoXYZ }>(
  impactos: readonly T[],
  planos: readonly PlanoNormalConstante[],
  tolerancia: number = TOLERANCIA_CORTE,
): T[] {
  if (planos.length === 0) return [...impactos]
  return impactos.filter((r) => pontoVisivel(r.point, planos, tolerancia))
}

/** O impacto mais próximo da câmara que esteja à vista, ou null.
 *
 *  A lista não tem de vir ordenada (o Fragments devolve por amostra, e
 *  com vários modelos a lista é a concatenação de todos). */
export function primeiroImpactoVisivel<
  T extends { point: PontoXYZ; distance: number },
>(
  impactos: readonly T[] | null | undefined,
  planos: readonly PlanoNormalConstante[],
  tolerancia: number = TOLERANCIA_CORTE,
): T | null {
  if (!impactos || impactos.length === 0) return null
  let melhor: T | null = null
  for (const r of impactos) {
    if (!pontoVisivel(r.point, planos, tolerancia)) continue
    if (!melhor || r.distance < melhor.distance) melhor = r
  }
  return melhor
}

/** O troço do raio que atravessa a parte visível: [entrada, saida] em
 *  parâmetro t (ponto = origem + t * direcção), já cortado a t >= 0.
 *  Null quando o raio não passa pela parte visível (e então não há nada
 *  para apanhar ali, por muito que haja geometria).
 *
 *  É o método das lajes generalizado a semiespaços: cada plano dá um
 *  limite inferior (o raio entra) ou superior (o raio sai) e o troço é
 *  a intersecção. Com a câmara DENTRO da caixa a entrada é 0. Sem
 *  planos, o troço é [0, infinito). */
export function intervaloVisivelDoRaio(
  origem: PontoXYZ,
  direccao: PontoXYZ,
  planos: readonly PlanoNormalConstante[],
  tolerancia: number = TOLERANCIA_CORTE,
): { entrada: number; saida: number } | null {
  let entrada = 0
  let saida = Number.POSITIVE_INFINITY
  for (const { normal: n, constant: c } of planos) {
    const d0 = n.x * origem.x + n.y * origem.y + n.z * origem.z + c
    const dd = n.x * direccao.x + n.y * direccao.y + n.z * direccao.z
    if (Math.abs(dd) < 1e-12) {
      // Raio paralelo ao plano: ou está todo do lado visível, ou todo
      // do lado cortado.
      if (d0 < -tolerancia) return null
      continue
    }
    // d(t) = d0 + t * dd; visível onde d(t) >= -tolerância.
    const t = (-tolerancia - d0) / dd
    if (dd > 0) entrada = Math.max(entrada, t)
    else saida = Math.min(saida, t)
    if (entrada > saida) return null
  }
  return { entrada, saida }
}
